import React, { useEffect, useState } from "react";
import {
  Card,
  Button,
  Typography,
  Spin,
  Table,
  Tooltip,
  Switch,
  Badge,
  Modal,
  Select,
  Form,
  InputNumber,
} from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { useTrainingDetail } from  "PlatForms/OrgLMS/Hooks/MentorHooks/useTrainingDetails";
import ProjectOverview from "./ProjectOverview";
import helper from "./tablesColumns";
import TaskModal from "./TaskModel";
import moment from "moment";
import api from "Common/Client/Interceptor";
import axios from "axios";

const { Title } = Typography;
const { Option } = Select;

const TrainingDetail = () => {
  const { id: trainingId } = useParams();
  const navigate = useNavigate();
  const [scoreForm] = Form.useForm();
  const token = localStorage.getItem("token");

  // Add state for score modal
  const [isScoreModalVisible, setIsScoreModalVisible] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [submittingScore, setSubmittingScore] = useState(false);
  const [selectedDownloadingId, setSelectedDownloadingId] = useState(null);

  const {
    isModalVisible,
    disabledDate,
    handleContentTypeChange,
    handleCancel,
    handleOk,
    loadingContent,
    submitting,
    contentItems,
    projectData,
    loading,
    contentType,
    form: taskForm,
    setIsModalVisible,
    fetchProjects,
    updating,
    handleToggleResubmission,
  } = useTrainingDetail(trainingId);

  // Group members were moved out of the parent detail into a separate
  // paginated endpoint; load them once the group id is available.
  const [members, setMembers] = useState([]);
  useEffect(() => {
    const groupId = projectData?.group?.id;
    if (!groupId) return;
    api
      .get("/TrainingGroup/Members/List", {
        params: { groupId, pageNumber: 1, pageSize: 30, orderByLatest: false },
      })
      .then((r) => setMembers(r.data.items || []))
      .catch(() => setMembers([]));
  }, [projectData?.group?.id]);

  const showModal = (record) => {
    taskForm.setFieldsValue({
      memberName: record.learnerName,
      memberId: record.id,
    });
    setIsModalVisible(true);
  };

  // Handle showing the score modal
  const showScoreModal = (record) => {
    setSelectedTask(record);
    setIsScoreModalVisible(true);
    scoreForm.setFieldsValue({
      taskId: record.id,
      score: 5, // Default score
    });
  };

  // Handle submitting the score
  const handleScoreSubmit = async () => {
    try {
      const values = await scoreForm.validateFields();
      setSubmittingScore(true);

      // Make API call
      const response = await api.post("/Training/Mentor/Task-Scores", values, {
        headers: {
          accept: "*/*",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      // Refresh the data
      fetchProjects();
      setIsScoreModalVisible(false);
    } catch (error) {
      console.error("Error submitting score:", error);
    } finally {
      setSubmittingScore(false);
    }
  };

  const { columns } = helper(showModal, projectData);

  const taskColumns = [
    { title: "Task", dataIndex: "title", key: "title" },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        let badgeStatus, text;

        switch (status) {
          case 1:
            badgeStatus = "error";
            text = "Pending";
            break;
          case 2:
            badgeStatus = "processing";
            text = "In Progress";
            break;
          case 3:
            badgeStatus = "success";
            text = "Submitted";
            break;
          case 4:
            badgeStatus = "success";
            text = "Reviewed & Scored";
            break;
          default:
            badgeStatus = "default";
            text = "Unknown";
        }

        return <Badge status={badgeStatus} text={text} />;
      },
    },
    {
      title: "Deadline",
      dataIndex: "deadline",
      key: "deadline",
      render: (text) => (text ? moment(text).format("DD-MM-YYYY") : "N/A"),
    },
    {
      title: "Submitted File",
      dataIndex: "taskAssignFileName",
      key: "taskAssignFileName",
      render: (_, record) => {
        if (record.status === 1) {
          return "No file submitted yet";
        }
        if (record.submittedFileName) {
          return (
            <Button
              type="link"
              style={{ padding: 0 }}
              onClick={() => handleDownloadFile(record.id, 2, record.submittedFileName)}
            >
              {selectedDownloadingId === record.id ? <Spin size="small" /> : "View Submitted File"}
            </Button>
          );
        }
        return "No file submitted";
      },
    },
    {
      title: "Score Assignment",
      key: "giveScore",
      render: (_, record) => {
        if (
          record.status === 4 &&
          record.mentorScore !== null &&
          record.mentorScore !== undefined
        ) {
          return record.mentorScore; // 👈 Show score
        }

        if (record.status === 3) {
          return (
            <Button type="primary" onClick={() => showScoreModal(record)}>
              Give Score
            </Button>
          );
        }

        return "---";
      },
    },
    {
      title: "Allow Resubmission",
      key: "enableSubmission",
      render: (_, record) => {
        const { status, enableSubmission } = record;

        let isDisabled = false;
        let tooltipText = "";

        if (status === 1) {
          isDisabled = true;
          tooltipText = "Task is not submitted yet!";
        } else if (status === 4) {
          isDisabled = true;
          tooltipText = "Score has already been assigned for this task!";
        }

        return (
          <Tooltip title={tooltipText}>
            <Switch
              checked={enableSubmission}
              onChange={() => handleToggleResubmission(record.id, enableSubmission)}
              disabled={updating === record.id || isDisabled}
              loading={updating === record.id}
            />
          </Tooltip>
        );
      },
    },
  ];

  const handleDownloadFile = async (taskId, fileType, fileName) => {
    try {
      setSelectedDownloadingId(taskId);
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/Training/Download-Task`,
        {},
        {
          params: {
            taskId,
            taskFileType: fileType,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "*/*",
          },
          responseType: "blob",
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName || "file");
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error("Download failed:", error.response || error.message);
    } finally {
      setSelectedDownloadingId(null);
    }
  };

  return (
    <Card
      className="card-professional"
      title="Training Overview"
      variant="borderless"
      style={{ marginBottom: 20 }}
      extra={
        <Button type="default" onClick={() => navigate(-1)}>
          {/* <Button type="default" onClick={() => navigate(`/assigned-group-detail/${trainingId}`)}> */}
          Back
        </Button>
      }
    >
      {loading ? (
        <Spin
          size="large"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "200px",
          }}
        />
      ) : (
        <>
          <ProjectOverview
            projectData={projectData}
            trainingId={trainingId}
            fetchProjects={fetchProjects}
          />

          <Title level={5} style={{ marginTop: 10 }}>
            <h4>{projectData?.group?.groupType === 2 ? "Group Members" : "Participant"}</h4>{" "}
          </Title>
          <Table
            bordered
            dataSource={members}
            columns={columns}
            rowKey="id"
            pagination={true}
            scroll={{ x: "max-content" }}
          />

          <Title level={3} style={{ marginTop: 20 }}>
            Task Management
          </Title>
          <Table
            columns={taskColumns}
            dataSource={projectData?.tasks || []}
            rowKey="id"
            pagination={true}
            bordered
            scroll={{ x: "max-content" }}
          />

          <TaskModal
            open={isModalVisible}
            onOk={handleOk}
            onCancel={handleCancel}
            confirmLoading={submitting}
            form={taskForm}
            contentType={contentType}
            contentItems={contentItems}
            loadingContent={loadingContent}
            disabledDate={disabledDate}
            onContentTypeChange={handleContentTypeChange}
            projectData={projectData}
          />

          {/* Score Modal */}
          <Modal
            title={`Give Score for Task: ${selectedTask?.title || ""}`}
            open={isScoreModalVisible}
            onOk={handleScoreSubmit}
            onCancel={() => setIsScoreModalVisible(false)}
            confirmLoading={submittingScore}
            centered
            okText="Submit"
          >
            <Form form={scoreForm} layout="vertical">
              <Form.Item name="taskId" hidden>
                <InputNumber />
              </Form.Item>
              <Form.Item
                name="score"
                label="Select Score"
                rules={[{ required: true, message: "Please select a score" }]}
              >
                <Select placeholder="Select a score">
                  {[1, 2, 3, 4, 5].map((score) => (
                    <Option key={score} value={score}>
                      {score}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Form>
          </Modal>
        </>
      )}
    </Card>
  );
};

export default TrainingDetail;
